import type { Draft, DraftRecipient, DraftSender } from "./inboxDraft.types";

interface ApplyDefaultsParams {
	drafts: Draft[];
	defaultFrom: DraftSender | undefined;
	defaultTo: DraftRecipient | undefined;
}

/**
 * A draft is considered to have a placeholder `from` when the resolution
 * step in `createDefaultDraft` could not assign a real sender (userId 0).
 * AI-injected drafts and drafts the user has manually edited always carry
 * a real userId, so they are left untouched.
 */
const isFromPlaceholder = (from: DraftSender | undefined): boolean => !from?.userId;

/**
 * Apply resolved default sender/recipient only to drafts whose `from` is a
 * placeholder. Drafts already populated by AI injection or user edits keep
 * their existing sender/recipient.
 *
 * Returns the same array reference when nothing changes so React's bailout
 * keeps the existing render stable.
 */
export const applyDefaultsToPlaceholderDrafts = ({
	drafts,
	defaultFrom,
	defaultTo,
}: ApplyDefaultsParams): Draft[] => {
	if (!defaultFrom) {
		return drafts;
	}

	let changed = false;

	const next = drafts.map((draft) => {
		if (!isFromPlaceholder(draft.from)) {
			return draft;
		}

		changed = true;

		const updated: Draft = {
			...draft,
			from: defaultFrom,
		};

		if (defaultFrom.type !== "email") {
			updated.cc = [];
			updated.bcc = [];
			updated.showCcBcc = false;
		}

		if (defaultTo) {
			updated.to = defaultTo;
		}

		return updated;
	});

	return changed ? next : drafts;
};
